/**
 * Providoor Concierge Availability Module
 * Rewritten version:
 * - Does not strip card display styles
 * - Hides cards/bars/FAB using a CSS class instead of inline display resets
 * - Preserves natural theme layout for collection/product grid cards
 * - Supports endpoint responses with either `available` or `active`
 */
(function() {
  'use strict';

  var ENDPOINT = window.__conciergeEndpoint || '';
  var PHONE = window.__conciergePhone || '';
  var POLL_INTERVAL = 30000;
  var state = { available: false };
  var HIDDEN_CLASS = 'concierge-is-hidden';
  var STYLE_ID = 'concierge-runtime-styles';

  function injectRuntimeStyles() {
    if (document.getElementById(STYLE_ID)) return;

    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = '.' + HIDDEN_CLASS + '{display:none !important;}';
    document.head.appendChild(style);
  }

  function getRegularBar() {
    return document.querySelector('[data-announcement-regular]');
  }

  function getConciergeBar() {
    return document.querySelector('[data-concierge-bar]');
  }

  function getConciergeCards() {
    return document.querySelectorAll('[data-concierge-card], .concierge-card');
  }

  function getCardButtons() {
    return document.querySelectorAll('[data-concierge-card-btn]');
  }

  function getFab() {
    return document.getElementById('concierge-fab');
  }

  function addHidden(el) {
    if (!el) return;
    el.classList.add(HIDDEN_CLASS);
    el.setAttribute('aria-hidden', 'true');
  }

  function removeHidden(el) {
    if (!el) return;
    el.classList.remove(HIDDEN_CLASS);
    el.removeAttribute('aria-hidden');
  }

  function setVisible(el, shouldShow) {
    if (!el) return;
    if (shouldShow) {
      removeHidden(el);
    } else {
      addHidden(el);
    }
  }

  function setNodeListVisible(nodeList, shouldShow) {
    if (!nodeList || !nodeList.length) return;

    nodeList.forEach(function(el) {
      setVisible(el, shouldShow);
    });
  }

  function normaliseAvailability(data) {
    if (!data || typeof data !== 'object') return false;
    if (typeof data.available !== 'undefined') return !!data.available;
    if (typeof data.active !== 'undefined') return !!data.active;
    return false;
  }

  function updateAnnouncementBars(isAvailable) {
    var regularBar = getRegularBar();
    var conciergeBar = getConciergeBar();

    // If concierge bar isn't present, do nothing.
    // This lets normal announcement functionality remain untouched.
    if (!conciergeBar) return;

    // The bars are server-rendered with an inline style="display:none;".
    // setVisible() only toggles a CSS class, which cannot override an inline
    // style, so we must set inline display directly here for the bar to show/hide.
    // Endpoint-driven: show the concierge bar only when Aircall reports available;
    // otherwise fall back to the regular announcement bar. (Ryan 2026-07-24)
    if (isAvailable) {
      setVisible(regularBar, false);
      if (regularBar) regularBar.style.display = 'none';
      setVisible(conciergeBar, true);
      conciergeBar.style.display = '';
    } else {
      setVisible(conciergeBar, false);
      conciergeBar.style.display = 'none';
      setVisible(regularBar, true);
      if (regularBar) regularBar.style.display = '';
    }
  }

  function updateConciergeCards(isAvailable) {
    var cards = getConciergeCards();
    var cardBtns = getCardButtons();

    setNodeListVisible(cards, isAvailable);

    cardBtns.forEach(function(btn) {
      var disabled = !isAvailable || !PHONE;

      if (!disabled) {
        btn.setAttribute('href', 'tel:' + PHONE);
        btn.removeAttribute('aria-disabled');
        btn.classList.remove('is-disabled');
      } else {
        btn.removeAttribute('href');
        btn.setAttribute('aria-disabled', 'true');
        btn.classList.add('is-disabled');
      }

      var cta = btn.querySelector('[data-concierge-card-cta]');
      if (cta) {
        cta.textContent = isAvailable ? '\u260E Call Now' : 'Concierge Offline';
      }

      var badge = btn.querySelector('[data-concierge-card-badge]');
      if (badge) {
        var textNodeUpdated = false;

        badge.childNodes.forEach(function(node) {
          if (node.nodeType === Node.TEXT_NODE) {
            node.textContent = isAvailable ? ' Concierge Online' : ' Currently Offline';
            textNodeUpdated = true;
          }
        });

        if (!textNodeUpdated) {
          badge.appendChild(
            document.createTextNode(isAvailable ? ' Concierge Online' : ' Currently Offline')
          );
        }
      }
    });
  }

  function updateFab(isAvailable) {
    var fab = getFab();
    if (!fab) return;

    fab.classList.toggle('concierge-fab--online', isAvailable);
    fab.classList.toggle('concierge-fab--offline', !isAvailable);

    setVisible(fab, isAvailable);

    if (isAvailable && PHONE) {
      fab.setAttribute('href', 'tel:' + PHONE);
      fab.style.pointerEvents = '';
      fab.style.opacity = '1';
      fab.style.visibility = 'visible';
      fab.removeAttribute('aria-disabled');
    } else {
      fab.removeAttribute('href');
      fab.style.pointerEvents = 'none';
      fab.style.opacity = '0';
      fab.style.visibility = 'hidden';
      fab.setAttribute('aria-disabled', 'true');
    }
  }

  function updateUI(data) {
    state = data || state;
    var isAvailable = !!state.available;

    updateAnnouncementBars(isAvailable);
    updateConciergeCards(isAvailable);
    updateFab(isAvailable);

    if (typeof gtag === 'function') {
      gtag('event', isAvailable ? 'concierge_banner_view' : 'concierge_offline_view');
    }
  }

  function buildStatusUrl() {
    if (!ENDPOINT || ENDPOINT.indexOf('http') !== 0) return '';
    return ENDPOINT + (ENDPOINT.indexOf('?') >= 0 ? '&' : '?') + '_=' + Date.now();
  }

  function checkStatus() {
    // Endpoint-driven: visibility follows LIVE Aircall agent availability.
    // window.__conciergeEndpoint -> Google Apps Script ?mode=status returns
    // {"available":true}. Simple GET (no custom headers) so no CORS preflight;
    // the endpoint sends Access-Control-Allow-Origin:*. (Ryan 2026-07-24)
    var url = buildStatusUrl();
    if (!url) {
      updateUI({ available: false });
      return;
    }
    fetch(url, { method: 'GET', cache: 'no-store' })
      .then(function(res) { return res.ok ? res.json() : null; })
      .then(function(data) { updateUI({ available: normaliseAvailability(data) }); })
      .catch(function() { updateUI({ available: false }); });
  }

  function trackClicks() {
    document.addEventListener('click', function(e) {
      if (typeof gtag !== 'function') return;

      if (e.target.closest('[data-concierge-phone]')) {
        gtag('event', 'concierge_banner_click');
      }

      if (e.target.closest('[data-concierge-card-btn]')) {
        gtag('event', 'concierge_menu_prompt_click');
      }

      if (e.target.closest('#concierge-fab')) {
        gtag('event', 'concierge_fab_click');
      }
    });
  }

  function init() {
    injectRuntimeStyles();
    trackClicks();

    var conciergeBar = getConciergeBar();
    var conciergeCards = getConciergeCards();
    var fab = getFab();

    // If this page/theme does not use concierge bar/cards,
    // leave existing announcement UI alone.
    if (!conciergeBar && !conciergeCards.length && !fab) {
      return;
    }

    // Safe initial state while endpoint loads
    if (conciergeBar) {
      addHidden(conciergeBar);
    }

    if (fab) {
      updateFab(false);
    }

    if (conciergeCards.length) {
      updateConciergeCards(false);
    }

    // Only hide the regular bar during load if a concierge bar exists
    if (conciergeBar) {
      var regularBar = getRegularBar();
      if (regularBar) addHidden(regularBar);
    }

    checkStatus();
    window.setInterval(checkStatus, POLL_INTERVAL);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();